import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import { useShopMode } from "../context/ShopModeContext";
import { BrandLogo } from "./BrandLogo";
import { BrandTmBadge } from "./BrandTmBadge";
import { FooterPolicyModal } from "./FooterPolicyModal";
import {
  Package, ShieldCheck, RefreshCcw, Headphones, Phone, Mail, Clock,
} from "lucide-react";
import { FaFacebookF, FaInstagram, FaXTwitter, FaYoutube, FaLinkedinIn, FaWhatsapp, FaPinterestP } from "react-icons/fa6";

const BENEFIT_ICON = {
  package: Package,
  shield: ShieldCheck,
  "refresh-ccw": RefreshCcw,
  headphones: Headphones,
};

export const Footer = () => {
  const { settings } = useSettings();
  const { isWholesale } = useShopMode();
  const navigate = useNavigate();
  const [policyOpen, setPolicyOpen] = useState(false);
  const [activePolicy, setActivePolicy] = useState("shipping");
  const [activeLinkData, setActiveLinkData] = useState(null);

  const s = settings || {};
  const T = s.texts || {};
  const socials = s.socials || {};
  const benefits = s.footer_benefits || [];
  const contact = s.contact || {};

  const rawLinks = s.quick_links;
  const links = (Array.isArray(rawLinks) && rawLinks.length > 0)
    ? rawLinks
    : [
        { label: "About Us", href: "/about" },
        { label: "Collections", href: "/products" },
        { label: "FAQs", href: "/faqs" },
        { label: "Shipping Policy", href: "/shipping-policy" },
        { label: "Return Policy", href: "/return-policy" },
        { label: "Terms & Conditions", href: "/terms" },
        { label: "Privacy Policy", href: "/privacy" },
        { label: "Contact Us", href: "#contact" },
      ];

  const status = s.social_status || {};
  const socialItems = [
    { icon: FaFacebookF, label: "Facebook", id: "facebook", href: socials.facebook, color: "text-[#1877F2]" },
    { icon: FaInstagram, label: "Instagram", id: "instagram", href: socials.instagram, color: "text-[#E1306C]" },
    { icon: FaXTwitter, label: "X", id: "twitter", href: socials.twitter, color: "text-[#ffffff]" },
    { icon: FaYoutube, label: "YouTube", id: "youtube", href: socials.youtube, color: "text-[#FF0000]" },
    { icon: FaLinkedinIn, label: "LinkedIn", id: "linkedin", href: socials.linkedin, color: "text-[#0A66C2]" },
    { icon: FaPinterestP, label: "Pinterest", id: "pinterest", href: socials.pinterest, color: "text-[#BD081C]" },
    { icon: FaWhatsapp, label: "WhatsApp", id: "whatsapp", href: socials.whatsapp, color: "text-[#25D366]" },
  ].filter((x) => x.href && status[x.id] !== false);

  const brandSubtitle = (() => {
    if (s.brand_tagline && s.brand_tagline.trim()) {
      const clean = s.brand_tagline.trim();
      if (/wholesale/i.test(clean)) {
        return isWholesale ? clean : clean.replace(/wholesale/gi, "RETAIL");
      }
      if (/retail/i.test(clean)) {
        return isWholesale ? clean.replace(/retail/gi, "WHOLESALE") : clean;
      }
      return `${isWholesale ? "WHOLESALE" : "RETAIL"} · ${clean}`;
    }
    return isWholesale ? "WHOLESALE · DIRECT FACTORY" : "RETAIL · COMPLETE LOOK";
  })();

  const handleLinkClick = (l, e) => {
    setActiveLinkData(l);
    const rawHref = (l.href || "").trim().toLowerCase();
    const rawLabel = (l.label || "").trim().toLowerCase();

    // Check for FAQ link
    if (rawHref === "/faqs" || rawHref === "/faq" || rawHref === "#faq" || rawHref === "#faqs" || rawLabel === "faqs" || rawLabel === "faq" || rawLabel.includes("frequently asked")) {
      e?.preventDefault?.();
      navigate("/faqs");
      return;
    }

    // Check for Shipping Policy
    if (rawHref === "/shipping-policy" || rawHref === "/shipping" || rawHref === "#shipping-policy" || rawHref === "#shipping" || rawLabel.includes("shipping")) {
      e?.preventDefault?.();
      navigate("/shipping-policy");
      return;
    }

    // Check for Return Policy
    if (rawHref === "/return-policy" || rawHref === "/refund-policy" || rawHref === "/returns" || rawHref === "#return-policy" || rawHref === "#refund-policy" || rawHref === "#return" || rawLabel.includes("return") || rawLabel.includes("refund")) {
      e?.preventDefault?.();
      navigate("/return-policy");
      return;
    }

    // Check for About Us
    if (rawHref === "/about" || rawHref === "/about-us" || rawHref === "#about" || rawLabel.includes("about")) {
      e?.preventDefault?.();
      navigate("/about");
      return;
    }

    // Check for Terms
    if (rawHref === "/terms" || rawHref === "/terms-and-conditions" || rawHref === "#terms" || rawLabel.includes("term")) {
      e?.preventDefault?.();
      navigate("/terms");
      return;
    }

    // Check for Privacy
    if (rawHref === "/privacy" || rawHref === "/privacy-policy" || rawHref === "#privacy" || rawLabel.includes("privacy")) {
      e?.preventDefault?.();
      navigate("/privacy");
      return;
    }

    // Check for Contact Us anchor
    if (rawHref === "#contact" || rawLabel.includes("contact")) {
      e?.preventDefault?.();
      const el = document.getElementById("footer-contact");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#D4AF37]", "transition-all", "rounded-xl", "p-2");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-[#D4AF37]", "p-2");
        }, 2000);
      }
      return;
    }

    // Check for external links
    if (l.href && (l.href.startsWith("http://") || l.href.startsWith("https://") || l.href.startsWith("mailto:") || l.href.startsWith("tel:"))) {
      return;
    }

    // Check for route navigation
    if (l.href && l.href.startsWith("/")) {
      e?.preventDefault?.();
      navigate(l.href);
      return;
    }

    // Default fallbacks
    if (rawLabel.includes("collection") || rawLabel.includes("product") || rawLabel.includes("jewel") || rawLabel.includes("shop")) {
      e?.preventDefault?.();
      navigate("/products");
      return;
    }

    if (rawLabel.includes("order") || rawLabel.includes("track")) {
      e?.preventDefault?.();
      navigate("/orders");
      return;
    }

    if (rawLabel.includes("custom") || rawLabel.includes("require") || rawLabel.includes("bulk")) {
      e?.preventDefault?.();
      navigate("/custom-request");
      return;
    }
  };

  return (
    <footer className="bg-[#0a0a0a] text-stone-300 mt-8" data-testid="footer">
      {/* Benefit strip (dark gold) — 4 items in ONE row on every screen */}
      {benefits.length > 0 && (
        <div className="bg-gradient-to-r from-[#3a2a0e] via-[#6b4d16] to-[#3a2a0e] border-y border-[#D4AF37]/25" data-testid="footer-benefits">
          <div className="max-w-7xl mx-auto px-2 sm:px-6 py-3 sm:py-6 grid grid-cols-4 gap-1 sm:gap-6">
            {benefits.slice(0, 4).map((b, i) => {
              const Icon = BENEFIT_ICON[b.icon] || Package;
              return (
                <div key={i} data-testid={`footer-benefit-${i}`} className="flex items-center gap-1.5 sm:gap-3 min-w-0">
                  <Icon className="w-4 h-4 sm:w-7 sm:h-7 text-[#FFDF73] shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-sm font-semibold text-[#FFDF73] truncate leading-tight">{b.title}</p>
                    <p className="text-[8px] sm:text-xs text-[#FFDF73]/70 truncate leading-tight">{b.subtitle}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10">
        <div>
          <div className="flex items-center gap-3.5 mb-3">
            <BrandLogo size={s.footer_logo?.size || 38} logoObject={s.footer_logo} />
            <div className="flex flex-col justify-center items-center text-center min-w-0">
              <span className="brand-title-text text-xl sm:text-3xl leading-none text-center inline-flex items-center">
                {(s.brand_name || "Zaveri Trunk").toUpperCase()}
                <BrandTmBadge size="md" variant="gold" />
              </span>
              <span className="brand-subtitle-text brand-subtitle-text-dark text-[9px] sm:text-[10.5px] mt-1 text-center w-full">
                {brandSubtitle}
              </span>
            </div>
          </div>
          <p className="text-xs sm:text-sm text-stone-400 leading-relaxed mt-4">{s.footer_about}</p>
          {s.show_socials !== false && socialItems.length > 0 && (
            <div className="flex flex-wrap gap-4 mt-5" data-testid="footer-socials">
              {socialItems.map((x) => (
                <a key={x.label} href={x.href} target="_blank" rel="noopener noreferrer" aria-label={x.label}
                  data-testid={`footer-social-${x.label.toLowerCase()}`}
                  className={`${x.color} hover:scale-110 transition-transform duration-200 p-1 flex items-center justify-center`}>
                  <x.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Nested grid for Quick Links and Contact Us side-by-side on mobile */}
        <div className="grid grid-cols-2 gap-4 md:col-span-2">
          <div>
            <h4 className="font-semibold text-[#D4AF37] mb-4 text-sm uppercase tracking-wider">{T.footer_quick_links || "Quick Links"}</h4>
            <ul className="space-y-2.5 text-sm text-stone-400">
              {links.map((l, i) => {
                const isExternal = l.href && (l.href.startsWith("http://") || l.href.startsWith("https://") || l.href.startsWith("mailto:") || l.href.startsWith("tel:"));
                if (isExternal) {
                  return (
                    <li key={i}>
                      <a
                        data-testid={`quick-link-${i}`}
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-[#D4AF37] transition-colors"
                      >
                        {l.label}
                      </a>
                    </li>
                  );
                }
                return (
                  <li key={i}>
                    <button
                      type="button"
                      data-testid={`quick-link-${i}`}
                      onClick={(e) => handleLinkClick(l, e)}
                      className="hover:text-[#D4AF37] transition-colors text-left cursor-pointer"
                    >
                      {l.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div id="footer-contact">
            <h4 className="font-semibold text-[#D4AF37] mb-4 text-sm uppercase tracking-wider">{T.footer_contact || "Contact Us"}</h4>
            <ul className="space-y-3 text-sm text-stone-400">
              {contact.phone && (
                <li className="flex items-start gap-2"><Phone className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={2} /><a href={`tel:${contact.phone}`} className="hover:text-[#D4AF37] transition-colors">{contact.phone}</a></li>
              )}
              {contact.email && (
                <li className="flex items-start gap-2"><Mail className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={2} /><a href={`mailto:${contact.email}`} className="hover:text-[#D4AF37] transition-colors break-all">{contact.email}</a></li>
              )}
              {contact.hours && (
                <li className="flex items-start gap-2"><Clock className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={2} /><span>{contact.hours}</span></li>
              )}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 py-4 text-center text-[11px] text-stone-500">
        © {new Date().getFullYear()} {s.brand_name || "Zaveri Trunk"}. All Rights Reserved.
      </div>

      {/* Interactive Policy & Informational Modal */}
      <FooterPolicyModal
        open={policyOpen}
        onOpenChange={setPolicyOpen}
        policyKey={activePolicy}
        linkData={activeLinkData}
      />
    </footer>
  );
};
export default Footer;
